import { defineStore } from 'pinia'

import EventBus, { VideoCallEvents } from '@shared/event-bus'
import { ErrorTypes } from '@shared/models/enums/http'
import { StoreState } from '@shared/models/interfaces/store'
import { IFileUpload } from '@shared/models/proto/base/file-upload'
import { IMessage } from '@shared/models/proto/base/messages'
import { Nullable } from '@shared/models/types/primary'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import { updateObjectInArray } from '@shared/utils/helpers/array/update-object-in-array'
import { mergeAttachmentToEmbeddedAttachment } from '@shared/utils/helpers/attachments/merge-attachment-to-embedded-attachment'
import { isNetworkError } from '@shared/utils/helpers/error-guards/common-errors'
import { createError } from '@shared/utils/helpers/errors/error-builder'
import { isOnline } from '@shared/utils/helpers/general/is-online'
import { mergeMessages } from '@shared/utils/helpers/merge-messages'
import { updateQuickSignInMessage } from '@shared/utils/helpers/messages/update-quick-sign-in-message'
import { useVideoCallNotifications } from '@shared/utils/helpers/video-call-notifications'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { Attachment } from '@protos/chat/attachment_pb'
import { Message } from '@protos/chat/message_pb'
import { MessageType } from '@protos/chat/message_type_pb'
import { QuickSignDocument } from '@protos/quick_sign_document_pb'

import { GetConversationStarterDTO } from '@pc/models/proto/dto/conversations'
import {
  MessageCreateDataDTO,
  MessageIndexDTO,
  MessageShowDTO,
} from '@pc/models/proto/dto/messages'
import { ConversationStarterRO } from '@pc/models/proto/ro/conversations'
import { MessageItemRO, MessagesIndexRO } from '@pc/models/proto/ro/messages'
import { useSocketStore } from '@pc/store/socket'
import { useVideoCallStore } from '@pc/store/video-call'

import { useConversationsStore } from './conversations'
import { useMessageAttachmentsStore } from './message-attachments'

export interface State {
  messages: StoreState<MessagesIndexRO>
  animateMessage?: number
  sendMessageState: StoreState<{
    data: Message
  }>
  isRefreshing: boolean
  isShowStarters: boolean
  starters: string[]
}

const state = (): State => ({
  messages: getInitialState(),
  sendMessageState: getInitialState(),
  animateMessage: undefined,
  isRefreshing: false,
  isShowStarters: false,
  starters: [],
})

export const apiTransport = new ApiTransport(resourcePath, 'pc')

export const useMessagesStore = defineStore('messages', {
  state,

  actions: {
    async fetchMessages(dto: {
      conversationId: number
      page?: number
      perPage?: number
    }) {
      this.messages = updateState(this.messages)
      try {
        const response = await apiTransport.get<
          MessageIndexDTO,
          MessagesIndexRO
        >('/messages', new MessageIndexDTO(dto), MessagesIndexRO)

        const { data: existingMessages = [] } = this.messages.data ?? {}
        const { meta, data: newMessages } = response ?? { meta: {}, data: [] }

        const byConversationId = (msg: IMessage): boolean =>
          msg.conversationId === dto.conversationId

        const mergedMessages = mergeMessages({
          array: [existingMessages.filter(byConversationId), newMessages],
        })

        this.messages = updateState(
          this.messages,
          new MessagesIndexRO({
            meta,
            data: mergedMessages,
          })
        )
      } catch (error) {
        this.messages = updateState(this.messages, error as Error)
        throw error
      }
    },

    async fetchMessage(messageId: number) {
      const params = new MessageShowDTO({ messageId })

      const item = await apiTransport.get<MessageShowDTO, MessageItemRO>(
        `/messages/${params.messageId}`,
        params,
        MessageItemRO
      )

      return item?.data
    },

    async fetchStarters(params: { conversationId: string }) {
      const { conversationStarters } = (await apiTransport.get<
        GetConversationStarterDTO,
        ConversationStarterRO
      >(
        'configs/conversation_starter',
        new GetConversationStarterDTO(params),
        ConversationStarterRO
      )) ?? { conversationStarters: [] }

      this.starters = conversationStarters
    },

    async sendMessage(
      message: Pick<
        MessageCreateDataDTO,
        'conversationId' | 'text' | 'messageType' | 'attachments' | 'payload'
      > & { createdAt?: string },
      files: IFileUpload[]
    ): Promise<void> {
      const socketStore = useSocketStore()
      const [file, ...restFiles] = files

      if (restFiles.length) {
        throw new Error('Only one file attachment per message is supported')
      }

      const dto = {
        files: file ? [{ ...file, content: new Uint8Array() }] : [],
        data: {
          ...message,
          notifyApi: true,
          alert: true,
          epochTime: Math.ceil(new Date().getTime() / 1000),
          createdAt: message.createdAt || new Date().toISOString(),
        },
      }

      const isOnlineUser = isOnline() && socketStore.isChannelsConnected
      this.sendMessageState = updateState(this.sendMessageState)

      try {
        if (!isOnlineUser) {
          const networkError = createError(ErrorTypes.NETWORK)
          throw networkError
        }
        const newMessage = await socketStore.sendChatMessage(dto)

        if (file) {
          const messageAttachmentsStore = useMessageAttachmentsStore()
          await messageAttachmentsStore.createAttachment({
            file,
            messageId: newMessage.id,
            notifyApi: true,
          })

          socketStore.pushFileUploadFinished(
            message.conversationId,
            newMessage.id
          )
        }
        if (message.messageType === MessageType.QUICK_SIGN) {
          socketStore.pushFileUploadFinished(
            message.conversationId,
            newMessage.id
          )
        }

        this.sendMessageState = updateState(this.sendMessageState, {
          data: newMessage,
        })
      } catch (error) {
        const serverError = createError(ErrorTypes.SERVER)
        const messageError = isNetworkError(error as Error)
          ? error
          : serverError

        this.sendMessageState = updateState(
          this.sendMessageState,
          messageError as Error
        )
        throw messageError
      }
    },

    addMessageToConversation(message?: Message, animated?: boolean) {
      const conversationsStore = useConversationsStore()
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const { data: existingList = [], meta } = this.messages.data ?? {}

      if (
        message &&
        message.conversationId === conversationsStore.currentConversationId
      ) {
        const mergedMessages = mergeMessages({
          array: [existingList, [message]],
        })
        this.messages = updateState(
          this.messages,
          new MessagesIndexRO({
            meta,
            data: mergedMessages,
          })
        )

        if (animated) {
          this.animateMessage = message.id
        }

        if (!videoCallConfigsStore.isPictureInPictureMode) {
          useVideoCallNotifications(message)
        }
      }
    },

    updateAiMessage(message: Message) {
      const { data: existingList = [], meta } = this.messages.data ?? {}

      const newMessages = updateObjectInArray(existingList, message, 'id')

      this.animateMessage = message.id

      this.messages = updateState(
        this.messages,
        new MessagesIndexRO({
          meta,
          data: newMessages,
        })
      )
    },

    handleSpecialMessages(message: Message) {
      const videoCallStore = useVideoCallStore()

      switch (message.messageType) {
        case MessageType.VIDEO_CHAT_DECLINE:
          EventBus.emit(VideoCallEvents.RedirectToConversation)
          break

        case MessageType.VIDEO_CHAT_REQUEST:
          videoCallStore.isCallIncomingShown = true
          break

        case MessageType.VIDEO_CHAT_CANCEL:
          videoCallStore.isCallIncomingShown = false
          videoCallStore.onHangUp()
          break
      }
    },

    updateAttachmentsById(targetId: number, newAttachments: Attachment[]) {
      const messageAttachment =
        mergeAttachmentToEmbeddedAttachment(newAttachments)
      if (messageAttachment) {
        const newMessages = this.messages.data?.data.map((item) => {
          if (item.id === targetId) {
            return { ...item, attachments: [messageAttachment] }
          }
          return item
        })

        const { data: existingList = [], meta } = this.messages.data ?? {}
        if (newMessages) {
          const mergedMessages = mergeMessages({
            array: [existingList, newMessages],
          })

          this.messages = updateState(
            this.messages,
            new MessagesIndexRO({
              meta,
              data: mergedMessages,
            })
          )
        }
      }
    },

    updateQuickSignDocumentInMessage(quickSignDocument: QuickSignDocument) {
      const quickSign = {
        id: quickSignDocument?.id,
        status: quickSignDocument?.status,
      }
      const { data: existingList = [], meta } = this.messages.data ?? {}
      const mergedMessages = updateQuickSignInMessage(
        existingList as Array<Message>,
        quickSign
      )
      this.messages = updateState(
        this.messages,
        new MessagesIndexRO({
          meta,
          data: mergedMessages,
        })
      )
    },

    resetSendMessageState() {
      this.sendMessageState = getInitialState()
    },
  },

  getters: {
    lastMessageIsVideoCallInvitation(state) {
      const lastMessage = state.messages?.data?.data?.[0]

      return lastMessage?.messageType === MessageType.VIDEO_CHAT_REQUEST
    },

    // Return type MUST be explicitly set when using "this" in getters
    // https://pinia.vuejs.org/core-concepts/getters.html#Accessing-other-getters
    lastVideoCallRoomId(state): Nullable<string> {
      const messages = state.messages.data?.data ?? []

      // Most recent first so we don't need to reverse
      const lastVideoChatRequest = messages.find(
        (message) => message.messageType === MessageType.VIDEO_CHAT_REQUEST
      )

      const roomId =
        lastVideoChatRequest?.payload?.content.case === 'userPayload'
          ? lastVideoChatRequest?.payload?.content.value?.option.case ===
            'rtcSession'
            ? lastVideoChatRequest?.payload?.content.value?.option.value.roomId
            : null
          : null

      return roomId
    },

    messageById: (state) => (messageId: number) =>
      state.messages.data?.data?.find((message) => message.id === messageId),
  },
})
